/**
 * ContextApiClient -- las llaves que otros asistentes tienen sobre el contexto.
 *
 * MISION CONTEXT-PLATFORM-V1-001, tarea CTX-M2-09.
 *
 * Contrato leido del codigo que CORRE en produccion, no del enunciado:
 * NexoAPI b234fc8, Controllers/AgentCredentialsController.cs, y produccion
 * responde 1.6.43+b234fc8 -- el mismo commit.
 *
 *   GET  /api/v1/context/agent-credentials?subject=&business=
 *        -> { success, data: CredentialListItem[], message, timestamp }
 *   POST /api/v1/context/agent-credentials/{id}/revoke  { reason }
 *        -> 200 { data: { id, revoked, alreadyRevoked } }
 *           400 si falta el motivo, 404 si la credencial no existe.
 *
 * La fila NUNCA trae la llave ni su hash: solo `keyPrefix`. Esta capa tampoco
 * los inventa ni los guarda.
 *
 * Sin [JsonPropertyName] en los DTOs y sin politica en Program.cs, el JSON sale
 * en camelCase. Aun asi, al LEER se toleran las dos formas: cuesta una linea y
 * protege. Al ESCRIBIR se manda la forma exacta del DTO (`reason`).
 *
 * FAIL-OPEN QUE DICE LA VERDAD POR CAUSA: un mensaje unico para todo hace dudar
 * de lo que si funciona, asi que se distingue sin sesion / sin permiso / no
 * publicado / dev sin API / sin red.
 *
 * @module infrastructure/api/ContextApiClient
 */

import { ApiBase } from './ApiBase.js';
import { AppConfig } from '../../config.js';

const ENDPOINT = '/api/v1/context/agent-credentials';
// LLAVES-PROPUESTAS-T1 (A #25316): las propuestas viven junto a la emision manual, no en /agents/credentials.
const PROPUESTAS = `${ENDPOINT}/proposals`;

/**
 * EL CATALOGO DE PERMISOS, ESPEJADO DE LA API.
 *
 * FUENTE: NexoAPI/Security/ContextScopes.cs (main 68645fd). El propio archivo lo
 * dice mejor que yo: "Un typo aca es un 403 silencioso alla, asi que NADIE
 * escribe el string del scope a mano". La API valida fail-closed contra ese
 * diccionario, y un nombre que no este en el se rechaza con invalid_scopes.
 *
 * POR ESO ESTA LISTA EXISTE, y por eso la pantalla NO tiene un campo de texto
 * para escribir permisos: hoy conviven dos grafias -- learning.write.observations
 * de M1, y learning.session.write mas learning.observation.write de M2 -- y
 * elegir la vieja da un 403 MUDO. Le paso a Josse hoy mismo: su tutor leia bien
 * y fallaba al registrar, sin decir por que.
 *
 * LA DEUDA, dicha en voz alta: ningun endpoint publica este catalogo. Lo busque
 * en los controladores y ContextScopes solo aparece dentro de atributos
 * RequireScope, nunca como respuesta. Asi que esto es una COPIA, y si algun dia
 * entra un scope septimo hay DOS sitios que tocar. Lo correcto de raiz seria un
 * GET que devuelva el catalogo con su riesgo; no lo abro yo porque es de la API
 * y esta tarea dice expresamente que no hay que construir backend.
 *
 * El texto de cada uno esta en lenguaje de dueno, no de programador: quien mira
 * esta pantalla decide a quien le da que, no lee documentacion.
 */
export const SCOPES = [
  { id: 'learning.read', riesgo: 'bajo',
    que: 'Leer el contexto y el pensum de la persona',
    donde: 'GET /learning/whoami y GET /learning/context' },
  { id: 'learning.write.observations', riesgo: 'medio',
    que: 'Cerrar la sesion con estimaciones (grafia de M1)',
    donde: 'POST /learning/sessions/close' },
  { id: 'learning.session.write', riesgo: 'medio',
    que: 'Abrir una sesion de estudio y cerrarla por id',
    donde: 'POST /learning/sessions y POST /learning/sessions/{id}/close' },
  { id: 'learning.observation.write', riesgo: 'medio',
    que: 'Anotar lo que midio dentro de una sesion',
    donde: 'POST /learning/sessions/{id}/observations' },
  { id: 'learning.export', riesgo: 'medio',
    que: 'Exportar TODO lo que Nexo sabe de la persona',
    donde: 'GET /learning/export' },
  { id: 'memory.write', riesgo: 'medio',
    que: 'Escribir hechos en la memoria que Argemiro usa despues',
    // DOS rutas, no una. Lo caso Argy-Q al dictaminar el #273 (#10775): yo
    // habia puesto solo /facts, y quien cruce esta etiqueta con lo que su
    // agente llama de verdad -que puede ser el batch- no la encontraria. Una
    // etiqueta incompleta en la columna que existe PARA cruzar es peor que no
    // ponerla: se le cree. Verificado en AgentMemoryController 324 y 373.
    //
    // Y al arreglarlo cruce los SEIS, no solo el que me marcaron: los otros
    // cinco ya nombran todas sus rutas. Este era el unico corto.
    donde: 'POST /agent-memory/facts y /facts/batch' },

  // LOS CUATRO QUE FALTABAN, Y EL AVISO DE ARRIBA SE CUMPLIO SOLO.
  //
  // La cabecera de este archivo ya decia que esto es una COPIA y que -si algun
  // dia entra un scope septimo hay DOS sitios que tocar-. Entraron CUATRO y
  // nadie toco el segundo. Medido: las llaves vivas usan decisiones.read,
  // salud.read y salud.write, y ninguno estaba aqui; colaboradores.mi_estado.read
  // entra con el pase de Brayan.
  //
  // POR QUE IMPORTA MAS DE LO QUE PARECE (lo levanto Argy-A, #12619): quien mire
  // Accesos veia una llave con permisos que la pantalla no sabia nombrar. No
  // podia entender que hace esa llave, ni quitarle uno sin quitarselos todos.
  //
  // Los textos y los riesgos NO los invente: salen de Security/ContextScopes.cs
  // en origin/main, que es la fuente unica de nombres, y las rutas de los
  // RequireScope de cada controlador. Un riesgo mal puesto aqui no es cosmetico:
  // RequireScopePolicy deniega cuando el riesgo del scope supera el maximo de la
  // credencial, asi que quien lea -medio- donde dice -bajo- va a creer que hace
  // falta reemitir una llave que sirve.
  { id: 'decisiones.read', riesgo: 'bajo',
    que: 'Leer la cola de decisiones que esperan a Josse: cuantas son y cuanto cuesta decidirlas',
    donde: 'GET /tasks/decisiones' },
  { id: 'salud.read', riesgo: 'bajo',
    que: 'Leer los numeros del trabajo de la casa. Agregados nuestros: ni plata de un negocio ni datos de una persona',
    donde: 'GET /salud/indicadores' },
  { id: 'colaboradores.mi_estado.read', riesgo: 'medio',
    que: 'Leer la ficha de UN colaborador: su puntualidad, su racha y sus conteos',
    donde: 'GET /colaboradores/mi-estado' },
  { id: 'salud.write', riesgo: 'bajo',
    // El UNICO de salud que escribe. Su autor anoto que lo habria puesto medio y
    // por que se quedo en bajo: lo que escribe son conteos de PRs de la casa. Se
    // deja como esta en la API -- cambiarlo obliga a reemitir la llave del bot --
    // y aqui se dice tal cual, sin suavizarlo.
    que: 'Entregar el conteo de PRs abiertos para que la foto del embudo se tome sola',
    donde: 'POST /salud/embudo' },

  // LOS TRECE QUE FALTABAN (9-oct, NEXO-MCP-V1-001). Medido con comm de los nombres: la API tenia 23 permisos y
  // esta lista 10. Lo levanto el pendiente de Josse de ampliar su consentimiento para el MCP: la pantalla no dejaba
  // marcar bitacora.read, tareas.read ni solicitud.write, y una llave viva con bitacora.write o cruces.autorizar salia
  // en Accesos con permisos que nadie sabia nombrar.
  //
  // Igual que los cuatro de arriba, NADA inventado: riesgo de RiesgoPorScope y texto del comentario de cada constante
  // en Security/ContextScopes.cs, y rutas de su RequireScope (o de McpAcceso para las herramientas del MCP), todo en
  // NexoAPI origin/main 8ef111a. check:permisos-contexto compara esta lista con la de la API, permiso por permiso.
  { id: 'bitacora.read', riesgo: 'bajo',
    que: 'Leer la bitacora: lo que la casa hizo y lo que esta haciendo',
    donde: 'herramientas del MCP estado_del_dia y bitacora_leer' },
  { id: 'bitacora.write', riesgo: 'bajo',
    // Bajo por tres frenos del servidor, no por el nombre: el origen lo pone la API, el titulo no puede ser una
    // orden ([STOP], [ASSIGN], [REVIEW-OK]...) y cada entrada queda auditada. Su autor anoto que lo habria puesto medio.
    que: 'Publicar reportes en la bitacora con su propio nombre; nunca ordenes',
    donde: 'POST /bitacora (solo con llave)' },
  { id: 'tareas.read', riesgo: 'bajo',
    que: 'Leer las tareas de la casa y su estado, y la respuesta de Ares a una solicitud',
    donde: 'herramientas del MCP tareas_leer, solicitud_estado y respuesta_leer' },
  { id: 'solicitud.write', riesgo: 'bajo',
    que: 'Dejarle una solicitud formal a Ares: nace como tarea suya y Ares decide si la toma',
    donde: 'herramienta del MCP solicitud_crear' },
  { id: 'nodos.read', riesgo: 'bajo',
    que: 'Leer el mapa de la casa (rutas, versiones, quien verifico); sin plata ni personas',
    donde: 'GET /Projects/items' },
  { id: 'arcade.indicadores.read', riesgo: 'medio',
    que: 'Leer los indicadores del Arcade de UN negocio para el Gremio: trae plata del negocio y dias de asistencia sin nombres',
    donde: 'GET /arcade/gremio/indicadores, /gerente y /personas' },
  // CONTEXTO-SCOPE-T1 (12-oct, Ares #25962): entro en la API con NEGOCIO-LECTURAS-MCP-T1 (ContextScopes.cs 203,
  // api 1.6.723) y esta lista no lo supo: la propuesta 5406baa3 lo pedia y #/contexto lo mostraba como desconocido,
  // sin casilla, y Josse no podia aprobarla. Riesgo y texto de RiesgoPorScope y del comentario de la constante.
  { id: 'negocio.lecturas.read', riesgo: 'medio',
    que: 'Leer asistencia (marcajes, tardanzas) y danos por colaborador de UN negocio, para asesorar al dueno: trae personas y pesos',
    donde: 'herramientas del MCP asistencia_negocio_leer y danos_negocio_leer' },
  // LECTURAS-FINANZAS-MCP-T1 (T50, Ares #26319 y #26321): NexoAPI c048e26 (PR 496), ContextScopes.cs 205-217 y
  // RiesgoPorScope 321-323. Ninguna llave recibe finanzas sin propuesta aprobada por Josse (D-111).
  { id: 'negocio.finanzas.read', riesgo: 'alto',
    que: 'Leer la plata de UN negocio (ingresos, ventas, egresos, compras, creditos, arqueo, saldos y reportes): trae plata y nombres de clientes (cartera)',
    donde: 'herramienta del MCP finanzas_negocio_leer' },
  { id: 'negocio.operacion.read', riesgo: 'medio',
    que: 'Leer la operacion de UN negocio (inventario, carrito, aprobaciones): existencias, movimientos y pedidos, sin la plata del dueno',
    donde: 'herramienta del MCP operacion_negocio_leer' },
  { id: 'negocio.aprobaciones.write', riesgo: 'alto',
    que: 'Decidir por conversacion lo que la caja de UN negocio pide aprobar (anulaciones, eliminaciones): APAGADO, ninguna llave lo tiene y encenderlo es letra de Josse',
    donde: 'herramientas del MCP solicitud_preparar y solicitud_decidir' },
  // MADERINCO-VOZ-T1 (API 529, 15-oct): presupuestos por conversacion, solo el negocio demo y APAGADOS.
  { id: 'negocio.presupuestos.read', riesgo: 'bajo',
    que: 'Leer los presupuestos y el catalogo de UN negocio por conversacion (precios de venta y textos, sin plata del dueno): APAGADO, ninguna llave lo tiene y solo el negocio demo',
    donde: 'herramientas del MCP presupuesto_leer y presupuesto_catalogo_buscar' },
  { id: 'negocio.presupuestos.write', riesgo: 'medio',
    que: 'Armar un BORRADOR de presupuesto de UN negocio por conversacion, de a un paso (no emite ni borra emitidos): APAGADO, solo el negocio demo',
    donde: 'herramientas del MCP del presupuesto por paso (crear, secciones, items, parametros)' },
  { id: 'banco.conciliacion', riesgo: 'medio',
    que: 'Subir el extracto del banco y leer las propuestas de conciliacion de UN negocio; decidir sigue siendo del dueno',
    donde: 'POST /banco/extracto y GET /banco/propuestas' },
  { id: 'colaboradores.como_va.read', riesgo: 'medio',
    que: 'Preguntar como va OTRA persona (su ficha), sin que ella este presente',
    donde: 'GET /colaboradores/como-va' },
  { id: 'colaboradores.pin.validate', riesgo: 'medio',
    que: 'Probar si un documento y un PIN se corresponden',
    donde: 'POST /colaboradores/validar-pin' },
  { id: 'decisiones.write', riesgo: 'medio',
    que: 'Abrir una decision en el buzon de Josse; no puede responderla',
    donde: 'POST /decisiones' },
  { id: 'notas.write', riesgo: 'medio',
    que: 'Dejarle una nota a un agente de la flota; el titulo lo pone el servidor',
    donde: 'GET /notas/destinatarios y POST /notas' },
  { id: 'whatsapp.onboarding.write', riesgo: 'medio',
    que: 'Avanzar el alta de un cliente nuevo por WhatsApp; puede crear la persona si el telefono no existia',
    donde: 'POST /wa/onboarding/paso' },
  { id: 'cruces.autorizar', riesgo: 'alto',
    // El unico ALTO: escribe en el inventario del Desktop cuando el interruptor esta encendido, y la API exige ademas
    // que el telefono sea del dueno del negocio de la llave.
    que: 'Autorizar los cruces dano-entrada que Argemiro propone: escribe en el inventario del Desktop',
    donde: 'POST /agente/cruces/autorizar' }
];

/**
 * RECETAS: EL CONJUNTO, NO LOS INGREDIENTES (CTX-M2-20).
 *
 * La regla la escribio Ares y es la leccion del dia: cuando el conjunto correcto
 * depende de conocer una HISTORIA, hay que ofrecer el conjunto.
 *
 * LA HISTORIA, para que no haya que sabersela: la llave del tutor de Josse
 * nacio a las 18:54 con TRES permisos bien escritos y estuvo rota hasta las
 * 19:10. No fue un typo -- la lista de arriba ya impide los typos --. Fue elegir
 * bien un conjunto INCOMPLETO, que es un error distinto y la lista sola no lo
 * mata. (Q se lo corrigio a Ares y me lo corrige a mi: yo tenia escrito en la
 * vista que la lista mataba "esa clase de error para siempre". Mata los typos.)
 *
 * POR QUE EL TUTOR NECESITA LOS CUATRO, medido y no supuesto: el openapi que el
 * tutor lee publica SEIS operaciones -- la lista blanca esta en
 * Services/ContextOpenApiSubsetBuilder.cs, lineas 49-54 de NexoAPI main 68645fd --
 * y cada una cruzada contra el RequireScope de su controlador da:
 *
 *   learning_whoami            -> learning.read
 *   learning_context           -> learning.read
 *   learning_session_start     -> learning.session.write
 *   learning_observation_append-> learning.observation.write
 *   learning_session_close     -> learning.session.write
 *   learning_closeSession      -> learning.write.observations
 *
 * Y AHI ESTA LA TRAMPA, que ninguno de nosotros habia escrito: hay DOS endpoints
 * de cerrar sesion y NO piden el mismo permiso. El de M2 (sessions/{id}/close)
 * pide learning.session.write; el de M1 (sessions/close, sin id) pide
 * learning.write.observations. El propio codigo de la API marca ese ultimo con
 * un comentario: "cierre con estimaciones (M1-11) EN USO HOY". O sea que el
 * cierre que el tutor usa de verdad es justo el que exigia el permiso ausente.
 * Tres permisos correctos dejaban al tutor sin poder cerrar POR LA VIA QUE USA.
 *
 * Verificado ademas contra la BD: la llave viva nxk_live_03a0 tiene exactamente
 * estos cuatro.
 *
 * Y AQUI ME CORRIJO, porque lo que tenia escrito era falso. Yo puse que la llave
 * de tres (nxk_live_f1b8) "es la que no cierra". Cierra: tiene
 * learning.session.write, que habilita POST /learning/sessions/{id}/close, el
 * cierre de M2. Lo que NO puede es cerrar por la via de M1 -- sessions/close,
 * sin id --, que es justamente la que el tutor de Josse usa hoy. Lo caso Argy-Q
 * al dictaminar el #273 (#10775).
 *
 * La diferencia no es de matiz: "no cierra" manda a buscar un problema de
 * sesiones que no existe. Lo que hay es UNA de las dos vias cerrada.
 */
export const RECETAS = [
  {
    id: 'tutor-chatgpt',
    nombre: 'Tutor de ChatGPT (M1+M2)',
    para: 'Lee el contexto, abre sesion, anota lo que midio y cierra por las dos vias',
    scopes: [
      'learning.read',
      'learning.session.write',
      'learning.observation.write',
      'learning.write.observations'
    ]
  },
  {
    // MCP de Nexo (9-oct, #23470 y CONTRATO-MCP-NEXO_V1): lo que pide la llave de Josse para el piloto con ChatGPT y
    // los dots. Cada uno cruzado contra McpAcceso.cs (8ef111a): estado_del_dia y bitacora_leer -> bitacora.read;
    // tareas_leer, solicitud_estado y respuesta_leer -> tareas.read; solicitud_crear -> solicitud.write; y la cola de
    // decisiones -> decisiones.read. Los cuatro son de riesgo bajo.
    id: 'mcp-nexo',
    nombre: 'MCP de Nexo (ChatGPT y dots)',
    para: 'Lee la bitacora, las tareas y las decisiones, y le deja solicitudes a Ares',
    scopes: [
      'bitacora.read',
      'tareas.read',
      'decisiones.read',
      'solicitud.write'
    ]
  }
];

/**
 * LO QUE LA RECETA NO CUBRE: los conjuntos que nadie previo.
 *
 * Una receta resuelve el caso conocido. Este aviso es para el resto, y mira lo
 * unico que hoy sabemos que se rompe en silencio: las DOS grafias de anotar
 * observaciones. Conviven porque son dos generaciones, no dos capacidades
 * distintas, y quien emite no tiene como saberlo.
 *
 * NO BLOQUEA, AVISA. Puede haber un agente legitimo que solo hable una de las
 * dos generaciones -- la llave de Q, nxk_live_6577, es exactamente eso --. Lo
 * que no puede pasar es que ocurra en silencio, que fue lo de hoy.
 *
 * @param {string[]} scopes los marcados ahora
 * @returns {{texto: string, falta: string}|null} null si no hay nada que decir
 */
export function revisarConjunto(scopes) {
  const tiene = (s) => scopes.includes(s);
  const M1 = 'learning.write.observations';
  const M2 = 'learning.observation.write';

  if (tiene(M2) && !tiene(M1)) {
    return { falta: M1, texto: 'Marcaste la grafia de M2 pero no la de M1. '
      + 'Un asistente que cierre la sesion por la via vieja (POST /learning/sessions/close) '
      + 'va a recibir un 403 mudo. Es exactamente lo que le paso al tutor hoy.' };
  }
  if (tiene(M1) && !tiene(M2)) {
    return { falta: M2, texto: 'Marcaste la grafia de M1 pero no la de M2. '
      + 'Este asistente no va a poder anotar dentro de una sesion '
      + '(POST /learning/sessions/{id}/observations).' };
  }
  return null;
}

/** Primer valor no nulo entre varias claves posibles. */
const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};

const texto = (v) => (v === null || v === undefined ? '' : String(v));
const lista = (v) => (Array.isArray(v) ? v.map(texto) : []);

/** Traduce el error de la API a una causa que se pueda escribir en pantalla. */
function motivoDe(res) {
  const txt = JSON.stringify(res?.error ?? '') + ' ' + texto(res?.status);
  if (/401|unauthor|sesion|token/i.test(txt)) {
    return { motivo: 'SIN_SESION', detalle: 'la sesion no tiene permiso para leer las llaves' };
  }
  if (/403|forbidden/i.test(txt)) {
    return { motivo: 'PROHIBIDO', detalle: 'esta pantalla es del dueno del contexto y de los devs' };
  }
  if (/404|not\s*found/i.test(txt)) {
    return { motivo: 'NO_PUBLICADO', detalle: 'el endpoint de llaves todavia no esta publicado en esta API' };
  }
  if (AppConfig.env === 'dev') {
    return { motivo: 'DEV_SIN_API', detalle: `el panel esta en dev y apunta a ${AppConfig.apiBase}; en produccion este endpoint si existe` };
  }
  return { motivo: 'ERROR_API', detalle: (typeof res?.error === 'string' ? res.error : res?.error?.message) || 'no pudimos hablar con la API ahora' };
}

/** Una fila de la lista, con los nombres que usa la vista. */
function filaDe(c) {
  return {
    id: texto(pick(c, 'id', 'Id')),
    agente: texto(pick(c, 'agentCode', 'agent_code', 'AgentCode')),
    nombre: texto(pick(c, 'name', 'Name')),
    prefijo: texto(pick(c, 'keyPrefix', 'key_prefix', 'KeyPrefix')),
    permisos: lista(pick(c, 'scopes', 'Scopes')),
    sujeto: texto(pick(c, 'subjectId', 'subject_id', 'SubjectId')),
    negocio: texto(pick(c, 'businessId', 'business_id', 'BusinessId')),
    riesgoMaximo: texto(pick(c, 'maxRiskLevel', 'max_risk_level', 'MaxRiskLevel')),
    proveedor: texto(pick(c, 'provider', 'Provider')),
    vence: pick(c, 'expiresAt', 'expires_at', 'ExpiresAt'),
    revocada: pick(c, 'revokedAt', 'revoked_at', 'RevokedAt'),
    ultimoUso: pick(c, 'lastUsedAt', 'last_used_at', 'LastUsedAt'),
    creada: pick(c, 'createdAt', 'created_at', 'CreatedAt')
  };
}

/** Una propuesta de llave (CredentialProposalDto de nexo-api 478), con los nombres que usa la vista. */
function propuestaDe(p) {
  return {
    id: texto(pick(p, 'id', 'Id')),
    agente: texto(pick(p, 'agentCode', 'agent_code', 'AgentCode')),
    nombre: texto(pick(p, 'name', 'Name')),
    permisos: lista(pick(p, 'scopes', 'Scopes')),
    sujeto: texto(pick(p, 'subjectId', 'subject_id', 'SubjectId')),
    negocio: texto(pick(p, 'businessId', 'business_id', 'BusinessId')),
    dias: Number(pick(p, 'dias', 'Dias')) || null,
    motivo: texto(pick(p, 'motivo', 'Motivo')),
    propuestaPor: texto(pick(p, 'propuestaPor', 'propuesta_por', 'PropuestaPor')),
    estado: texto(pick(p, 'estado', 'Estado')),
    creada: pick(p, 'creadaAt', 'creada_at', 'CreadaAt'),
    ultimoError: texto(pick(p, 'ultimoError', 'ultimo_error', 'UltimoError'))
  };
}

/** Los rechazos de las propuestas por su codigo HTTP (en `code`), no por el texto. */
function motivoDePropuesta(res) {
  // Sin red ApiBase deja code 'NETWORK'/'TIMEOUT' y el texto del navegador en ingles ("Failed to fetch"): se dice aqui.
  if (res?.code === 'NETWORK' || res?.code === 'TIMEOUT') return { motivo: 'SIN_RED', detalle: 'no hay conexion con la API ahora; vuelve a intentar en un momento' };
  const status = Number(res?.code);
  if (status === 401) return { motivo: 'SIN_SESION', detalle: 'la sesion vencio: vuelve a entrar' };
  if (status === 403) return { motivo: 'PROHIBIDO', detalle: 'aprobar llaves es del dueno del contexto (sesion de admin)' };
  if (status === 404) return { motivo: 'NO_PUBLICADO', detalle: 'las llaves propuestas todavia no estan publicadas en esta API (nexo-api 478)' };
  if (AppConfig.env === 'dev') return { motivo: 'DEV_SIN_API', detalle: `el panel esta en dev y apunta a ${AppConfig.apiBase}` };
  return { motivo: 'ERROR_API', detalle: (typeof res?.error === 'string' && res.error) || 'no pudimos hablar con la API ahora' };
}

export const ContextApiClient = {
  /**
   * Emite una llave nueva. La llave completa vuelve UNA SOLA VEZ.
   *
   * Contrato leido del codigo que corre (NexoAPI main 68645fd,
   * Controllers/AgentCredentialsController.cs y Models/AgentCredentialDtos.cs):
   *   POST /api/v1/context/agent-credentials
   *   { agentCode, subjectId?, businessId?, scopes[], name, days, provider? }
   *   -> { id, keyPrefix, key }   <- `key` no se guarda ni se vuelve a mostrar
   *
   * Los cinco rechazos que el controlador declara se traducen a una causa que
   * se pueda leer en pantalla. Nunca lanza.
   */
  async createCredential({ agente, nombre, sujeto, negocio, scopes, dias, proveedor } = {}) {
    const cuerpo = {
      agentCode: texto(agente).trim(),
      name: texto(nombre).trim(),
      scopes: Array.isArray(scopes) ? scopes.map(texto) : [],
      days: Number(dias) > 0 ? Number(dias) : 90
    };
    if (texto(sujeto).trim()) cuerpo.subjectId = texto(sujeto).trim();
    if (texto(negocio).trim()) cuerpo.businessId = texto(negocio).trim();
    if (texto(proveedor).trim()) cuerpo.provider = texto(proveedor).trim();

    // Se valida aqui lo que la API exige, para no gastar el viaje ni dejar que
    // el usuario vea un 400 generico por algo que se sabia antes de salir.
    if (!cuerpo.agentCode || !cuerpo.name) {
      return { ok: false, motivo: 'FALTAN_CAMPOS', detalle: 'el agente y el nombre son obligatorios' };
    }
    if (!cuerpo.subjectId && !cuerpo.businessId) {
      return { ok: false, motivo: 'SIN_DESTINO', detalle: 'hace falta una persona o un negocio: la llave siempre es sobre alguien' };
    }
    if (!cuerpo.scopes.length) {
      return { ok: false, motivo: 'SIN_PERMISOS', detalle: 'marca al menos un permiso' };
    }

    let res;
    try {
      res = await ApiBase.post(ENDPOINT, cuerpo);
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }
    if (!res.ok) {
      const txt = JSON.stringify(res.error ?? '') + ' ' + texto(res.status);
      if (/invalid_scopes/i.test(txt)) {
        return { ok: false, motivo: 'PERMISOS_INVALIDOS',
          detalle: 'la API no reconoce alguno de esos permisos. La lista de esta pantalla puede haber quedado vieja respecto a ContextScopes.cs' };
      }
      if (/agent_not_found/i.test(txt)) {
        return { ok: false, motivo: 'AGENTE_NO_EXISTE', detalle: 'ese codigo de agente no esta registrado' };
      }
      if (/consent_missing/i.test(txt)) {
        // ApiBase deja el cuerpo CRUDO del error en `details`; `error` es solo el
        // mensaje, una cadena. Lo lei mal la primera vez y la lista de consentimientos
        // que faltan -- que es lo unico util de este rechazo -- se perdia en silencio.
        // Medido: res.details.missing. Se toleran las dos formas por si cambia.
        const faltan = lista(res.details?.missing ?? res.error?.missing ?? []);
        return { ok: false, motivo: 'FALTA_CONSENTIMIENTO',
          detalle: 'falta consentimiento de la persona para: ' + (faltan.join(', ') || '(no lo dijo)'), faltan };
      }
      if (/target_required/i.test(txt)) {
        return { ok: false, motivo: 'SIN_DESTINO', detalle: 'la API pide una persona o un negocio' };
      }
      return { ok: false, ...motivoDe(res) };
    }

    const d = res.data?.data ?? res.data ?? {};
    return {
      ok: true,
      id: texto(pick(d, 'id', 'Id')),
      prefijo: texto(pick(d, 'keyPrefix', 'key_prefix', 'KeyPrefix')),
      llave: texto(pick(d, 'key', 'Key'))
    };
  },
  /**
   * Las llaves emitidas. Sin `subject` la API devuelve TODAS las del sistema
   * (medido en el SQL de ListAsync: los filtros son opcionales), asi que la
   * vista tiene que decir en pantalla que lista esta mostrando.
   * Nunca lanza.
   * @param {{subject?:string, business?:string}} [filtros]
   */
  async listCredentials(filtros = {}) {
    const q = [];
    if (filtros.subject) q.push(`subject=${encodeURIComponent(filtros.subject)}`);
    if (filtros.business) q.push(`business=${encodeURIComponent(filtros.business)}`);
    const ruta = q.length ? `${ENDPOINT}?${q.join('&')}` : ENDPOINT;

    let res;
    try {
      res = await ApiBase.get(ruta);
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }
    if (!res.ok) return { ok: false, ...motivoDe(res) };

    const d = res.data?.data ?? res.data ?? [];
    const arr = Array.isArray(d) ? d : (Array.isArray(d.items) ? d.items : []);
    return { ok: true, credenciales: arr.map(filaDe) };
  },

  /**
   * Quita una llave. El motivo es OBLIGATORIO en la API (400 sin el), asi que
   * se valida aqui antes de gastar el viaje.
   * Es idempotente: revocar una ya revocada tambien responde 200 y lo dice en
   * `yaEstaba`.
   * @param {string} id
   * @param {string} motivo
   */
  async revokeCredential(id, motivo) {
    const reason = texto(motivo).trim();
    if (!id) return { ok: false, motivo: 'SIN_ID', detalle: 'no se dijo que llave quitar' };
    if (!reason) return { ok: false, motivo: 'SIN_MOTIVO', detalle: 'escribe por que la quitas: sin motivo no queda rastro util' };

    let res;
    try {
      res = await ApiBase.post(`${ENDPOINT}/${encodeURIComponent(id)}/revoke`, { reason });
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }
    if (!res.ok) {
      const txt = JSON.stringify(res.error ?? '');
      if (/credential_not_found|404/i.test(txt)) {
        return { ok: false, motivo: 'NO_EXISTE', detalle: 'esa llave ya no esta en el sistema' };
      }
      if (/reason_required/i.test(txt)) {
        return { ok: false, motivo: 'SIN_MOTIVO', detalle: 'la API exige el motivo' };
      }
      return { ok: false, ...motivoDe(res) };
    }

    const d = res.data?.data ?? res.data ?? {};
    return { ok: true, yaEstaba: !!pick(d, 'alreadyRevoked', 'already_revoked') };
  },

  /*
   * LAS LLAVES PROPUESTAS POR ARES (LLAVES-PROPUESTAS-T1; contrato de A #25316, nexo-api 478 22191d0,
   * AgentCredentialProposalsController). Ares deja la especificacion por psql, SIN secreto; Josse aprueba aqui y solo
   * entonces el servidor emite. Las tres nunca lanzan. El estado HTTP se lee de `code` (ApiBase lo deja ahi) y no del
   * texto: un 404 sin cuerpo -- la ruta sin publicar -- no trae "not found" en el mensaje.
   */

  /** Las propuestas (por defecto solo las que esperan decision). */
  async listarPropuestas(estado = 'propuesta') {
    const ruta = estado ? `${PROPUESTAS}?estado=${encodeURIComponent(estado)}` : PROPUESTAS;
    let res;
    try {
      res = await ApiBase.get(ruta);
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }
    if (!res.ok) return { ok: false, ...motivoDePropuesta(res) };
    const d = res.data?.data ?? res.data ?? [];
    const arr = Array.isArray(d) ? d : (Array.isArray(d.items) ? d.items : []);
    return { ok: true, propuestas: arr.map(propuestaDe) };
  },

  /** Aprueba y emite. La llave completa vuelve UNA SOLA VEZ y no se guarda en ninguna parte. */
  async aprobarPropuesta(id) {
    if (!id) return { ok: false, motivo: 'SIN_ID', detalle: 'no se dijo que propuesta aprobar' };
    let res;
    try {
      res = await ApiBase.post(`${PROPUESTAS}/${encodeURIComponent(id)}/aprobar`, {});
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }
    if (!res.ok) {
      const cuerpo = res.details && typeof res.details === 'object' ? res.details : {};
      const msg = texto(pick(cuerpo, 'message', 'Message'));
      const code = texto(pick(cuerpo, 'code', 'Code'));
      if (msg === 'consent_missing') {
        const faltan = lista(pick(cuerpo, 'missing', 'Missing') ?? []);
        return { ok: false, motivo: 'FALTA_CONSENTIMIENTO', faltan,
          detalle: 'falta consentimiento de la persona para: ' + (faltan.join(', ') || '(no lo dijo)') };
      }
      if (code === 'NO_ESTA_EN_PROPUESTA') {
        return { ok: false, motivo: 'YA_DECIDIDA', detalle: 'esa propuesta ya fue ' + (texto(pick(cuerpo, 'estado', 'Estado')) || 'decidida') + ': recarga la lista' };
      }
      if (code === 'LLAVE_REPETIDA') {
        return { ok: false, motivo: 'LLAVE_REPETIDA', detalle: 'ya hay una llave activa con el mismo agente, permisos y destino' };
      }
      if (msg === 'agent_not_found') return { ok: false, motivo: 'AGENTE_NO_EXISTE', detalle: 'ese codigo de agente no esta registrado' };
      if (msg === 'propuesta_no_encontrada') return { ok: false, motivo: 'NO_EXISTE', detalle: 'esa propuesta ya no esta' };
      return { ok: false, ...motivoDePropuesta(res) };
    }
    const d = res.data?.data ?? res.data ?? {};
    return {
      ok: true,
      id: texto(pick(d, 'id', 'Id')),
      prefijo: texto(pick(d, 'keyPrefix', 'key_prefix', 'KeyPrefix')),
      llave: texto(pick(d, 'key', 'Key')),
      vence: pick(d, 'expiresAt', 'expires_at', 'ExpiresAt')
    };
  },

  /** Rechaza una propuesta. El motivo es obligatorio en la API (400 sin el): se valida aqui antes del viaje. */
  async rechazarPropuesta(id, motivo) {
    const m = texto(motivo).trim();
    if (!id) return { ok: false, motivo: 'SIN_ID', detalle: 'no se dijo que propuesta rechazar' };
    if (!m) return { ok: false, motivo: 'SIN_MOTIVO', detalle: 'escribe por que la rechazas: Ares la va a leer' };
    let res;
    try {
      res = await ApiBase.post(`${PROPUESTAS}/${encodeURIComponent(id)}/rechazar`, { motivo: m });
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }
    if (!res.ok) {
      const code = texto(pick(res.details && typeof res.details === 'object' ? res.details : {}, 'code', 'Code'));
      if (code === 'NO_ESTA_EN_PROPUESTA') return { ok: false, motivo: 'YA_DECIDIDA', detalle: 'esa propuesta ya fue decidida: recarga la lista' };
      return { ok: false, ...motivoDePropuesta(res) };
    }
    return { ok: true };
  },

  /**
   * EL CONSENTIMIENTO DE UNA PERSONA (MCP-NEXO-OAUTH-T1, Ares #23490): que permisos concede, quien y cuando lo cambio,
   * con sus palabras, y el historial. Contrato leido del codigo de nexo-api PR 447 (d6a4145,
   * ContextSubjectsController.cs):
   *   GET /api/v1/context/subjects/{id}/consent  [RequireAdmin]
   *   -> { subjectId, nombre, consent: { scopes[], granted_at, granted_by, texto_de_josse, historial[], revoked_at? } }
   *   404 subject_not_found
   * Es de la sesion de admin: una llave nxk_ recibe 403. Nunca lanza.
   */
  async leerConsentimiento(sujeto) {
    const id = texto(sujeto).trim();
    if (!id) return { ok: false, motivo: 'SIN_SUJETO', detalle: 'falta el id de la persona' };
    let res;
    try {
      res = await ApiBase.get(`${SUJETOS}/${encodeURIComponent(id)}/consent`);
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }
    if (!res.ok) return { ok: false, ...motivoDelConsentimiento(res) };
    const d = res.data?.data ?? res.data ?? {};
    const c = pick(d, 'consent', 'Consent') || {};
    const historial = Array.isArray(c.historial) ? c.historial : [];
    return {
      ok: true,
      sujeto: texto(pick(d, 'subjectId', 'SubjectId')) || id,
      nombre: texto(pick(d, 'nombre', 'Nombre')),
      scopes: lista(c.scopes),
      cambiadoEl: c.granted_at || null,
      cambiadoPor: texto(c.granted_by),
      texto: texto(c.texto_de_josse),
      revocado: !!c.revoked_at,
      historial: historial.map((h) => ({
        el: h?.at || null, por: texto(h?.por), agregados: lista(h?.agregados), quitados: lista(h?.quitados), texto: texto(h?.texto)
      })).reverse()
    };
  },

  /**
   * Agrega o quita permisos del consentimiento, con el TEXTO de quien consiente (la API lo exige: 10 letras o mas).
   *   PUT /api/v1/context/subjects/{id}/consent  { agregar[], quitar[], texto }
   *   -> { subjectId, scopes[], agregados[], quitados[] }
   *   400 SIN_CAMBIO | CAMBIO_CONTRADICTORIO | SCOPE_DESCONOCIDO | SIN_TEXTO
   *   409 CONSENTIMIENTO_REVOCADO | CONSENTIMIENTO_ILEGIBLE | subject_inactive ; 404 subject_not_found
   * Lo que la API exige se valida aqui antes del viaje y se dice QUE falta. Nunca lanza.
   */
  async cambiarConsentimiento(sujeto, { agregar = [], quitar = [], texto: palabras = '' } = {}) {
    const id = texto(sujeto).trim();
    const cuerpo = { agregar: lista(agregar), quitar: lista(quitar), texto: texto(palabras).trim() };
    if (!id) return { ok: false, motivo: 'SIN_SUJETO', detalle: 'falta el id de la persona' };
    if (!cuerpo.agregar.length && !cuerpo.quitar.length) {
      return { ok: false, motivo: 'SIN_CAMBIO', detalle: 'no cambiaste ningun permiso' };
    }
    if (cuerpo.texto.length < TEXTO_MINIMO_CONSENTIMIENTO) {
      return { ok: false, motivo: 'SIN_TEXTO',
        detalle: `escribe con tus palabras que autorizas y para que (${TEXTO_MINIMO_CONSENTIMIENTO} letras o mas): queda guardado con el cambio` };
    }
    let res;
    try {
      res = await ApiBase.put(`${SUJETOS}/${encodeURIComponent(id)}/consent`, cuerpo);
    } catch (err) {
      return { ok: false, motivo: 'SIN_RED', detalle: err?.message || 'no se pudo hablar con la API' };
    }
    if (!res.ok) return { ok: false, ...motivoDelConsentimiento(res) };
    const d = res.data?.data ?? res.data ?? {};
    return { ok: true, scopes: lista(pick(d, 'scopes', 'Scopes')), agregados: lista(pick(d, 'agregados', 'Agregados')),
      quitados: lista(pick(d, 'quitados', 'Quitados')) };
  }
};

const SUJETOS = '/api/v1/context/subjects';
/** Igual que ConsentimientoDelSujeto.TextoMinimo en la API (PR 447). */
export const TEXTO_MINIMO_CONSENTIMIENTO = 10;

/**
 * Que cambio entre lo consentido y lo marcado en pantalla: { agregar, quitar }. Sin DOM (lo prueba
 * check:consentimiento-contexto). Solo se mandan las diferencias: lo que ya estaba y sigue marcado no viaja.
 */
export function cambioDeConsentimiento(antes, marcados) {
  const a = new Set(lista(antes)), m = new Set(lista(marcados));
  return { agregar: [...m].filter((s) => !a.has(s)), quitar: [...a].filter((s) => !m.has(s)) };
}

/** Los rechazos del consentimiento, dichos para quien decide. El codigo de la API viaja en el cuerpo (`details`). */
function motivoDelConsentimiento(res) {
  const cuerpo = res?.details ?? (typeof res?.error === 'object' ? res.error : null) ?? {};
  const codigo = texto(cuerpo.message ?? cuerpo.code ?? '') + ' ' + JSON.stringify(res?.error ?? '');
  const detalle = texto(cuerpo.detail ?? cuerpo.Detail ?? '');
  const casos = [
    [/subject_not_found/i, 'NO_EXISTE', 'no hay ninguna persona con ese id'],
    [/subject_inactive/i, 'INACTIVA', 'esa persona ya no tiene vinculo activo (se dio de baja)'],
    [/CONSENTIMIENTO_REVOCADO/, 'REVOCADO', 'este consentimiento esta revocado: reabrirlo es otra decision, no un cambio de permisos'],
    [/CONSENTIMIENTO_ILEGIBLE/, 'ILEGIBLE', 'el consentimiento guardado esta danado; se corrige a mano, no encima'],
    [/SCOPE_DESCONOCIDO/, 'PERMISO_DESCONOCIDO', detalle || 'la API no conoce alguno de esos permisos'],
    [/CAMBIO_CONTRADICTORIO/, 'CONTRADICTORIO', detalle || 'un permiso no puede agregarse y quitarse a la vez'],
    [/SIN_TEXTO/, 'SIN_TEXTO', detalle || 'falta el texto de quien consiente'],
    [/SIN_CAMBIO/, 'SIN_CAMBIO', 'no cambiaste ningun permiso']
  ];
  for (const [re, motivo, d] of casos) if (re.test(codigo)) return { motivo, detalle: d };
  // Un 404 SIN subject_not_found es la RUTA que no existe: la API servida aun no trae el PR 447. El estado HTTP viaja
  // en `code` (CrudResult.fail), no en `status`: medido, no supuesto.
  if (Number(res?.code ?? res?.status) === 404) {
    return { motivo: 'NO_PUBLICADO', detalle: 'la API servida todavia no tiene la ruta del consentimiento (nexo-api PR 447)' };
  }
  return motivoDe(res);
}
